import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import type * as atlasTypes from 'azure-maps-control';
import {environment} from '../../environments/environment';

let atlasPromise: Promise<typeof atlasTypes> | null = null;
const getAtlas = (): Promise<typeof atlasTypes> => {
    if (atlasPromise === null) {
        atlasPromise = import('azure-maps-control');
    }
    return atlasPromise;
};

const DEFAULT_CENTER: [number, number] = [5.5, 52];
const DEFAULT_ZOOM = 7;
const LOCATION_ZOOM = 15;


@Component({
    selector: 'app-azure-map',
    templateUrl: './azure-map.component.html',
    styleUrls: ['./azure-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
})
export class AzureMapComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() location: string | null = null;
    @Input() interactive = false;

    @Output() pinMoved = new EventEmitter<[number, number]>();

    @ViewChild('azureMap', {static: true}) private mapElement: ElementRef<HTMLElement>;

    private map: atlasTypes.Map | null = null;
    private marker: atlasTypes.HtmlMarker | null = null;
    private atlas: typeof atlasTypes | null = null;

    private ignoreNextLocationChange = false;

    async ngAfterViewInit(): Promise<void> {
        this.atlas = await getAtlas();

        const coordinates = AzureMapComponent.parseLocation(this.location);
        this.map = new this.atlas.Map(this.mapElement.nativeElement, {
            language: 'nl-NL',
            showLogo: false,
            authOptions: {
                authType: this.atlas.AuthenticationType.subscriptionKey,
                subscriptionKey: environment.azureMapsKey
            },
            center: coordinates ?? DEFAULT_CENTER,
            zoom: coordinates ? LOCATION_ZOOM : DEFAULT_ZOOM,
            disableTelemetry: true
        });

        this.map.controls.add(new this.atlas.control.ZoomControl(), {
            position: this.atlas.ControlPosition.BottomRight
        });

        if (coordinates) {
            this.map.events.add('ready', () => this.placeMarker(coordinates));
        }

        if (this.interactive) {
            this.map.events.add('click', (e: atlasTypes.MapMouseEvent) => this.onMapClick(e));
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['location'] || changes['location'].firstChange || !this.map || !this.atlas) {
            return;
        }

        if (this.ignoreNextLocationChange) {
            this.ignoreNextLocationChange = false;
            return;
        }

        const coordinates = AzureMapComponent.parseLocation(this.location);
        if (coordinates) {
            this.placeMarker(coordinates);
        }
    }

    ngOnDestroy(): void {
        this.map?.dispose();
        this.map = null;
    }

    private onMapClick(e: atlasTypes.MapMouseEvent): void {
        if (!e.position) {
            return;
        }

        const [longitude, latitude] = e.position;
        this.updateMarker([longitude, latitude]);
        this.ignoreNextLocationChange = true;
        this.pinMoved.emit([latitude, longitude]);
    }

    private placeMarker(coordinates: [number, number]): void {
        this.updateMarker(coordinates);
        this.map.setCamera({center: coordinates, zoom: LOCATION_ZOOM});
    }

    /** Moves the marker without touching the camera, so a click doesn't fight the user's own zoom/pan. */
    private updateMarker(coordinates: [number, number]): void {
        if (this.marker) {
            this.map.markers.remove(this.marker);
        }

        this.marker = new this.atlas.HtmlMarker({
            color: '#008cbc',
            position: coordinates
        });
        this.map.markers.add(this.marker);
    }

    private static parseLocation(location: string | null): [number, number] | null {
        if (!location) {
            return null;
        }

        const [latitude, longitude] = location.split(',').map((part) => parseFloat(part.trim()));
        if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
            return null;
        }

        return [longitude, latitude];
    }
}
